/**
 * Chart display options (TradingView-style backlog slice: crosshair + main price scale).
 *
 * Implemented: CrosshairMode and PriceScaleMode on the chart’s default right price scale
 * (see lightweight-charts `applyOptions` on the chart instance).
 *
 * Remaining gaps vs a full TV-style spec (not in this slice):
 * - Per-pane price scale modes (indicators often keep linear scales while main is % / log).
 * - “Dot” crosshair style (separate from mode; may need custom overlay).
 * - Auto vs manual price range UI (library supports; not wired).
 * - Time zone, session breaks, timeframe resampling (data + time scale).
 *
 * File touch list for future work: [Chart.js](Chart.js), [ChartMirrorPane.js](ChartMirrorPane.js),
 * optional helpers here for new enums.
 *
 * Enum numeric values match `CrosshairMode` / `PriceScaleMode` in lightweight-charts v5.
 */

/** CrosshairMode-compatible values (lightweight-charts v5). */
export const CROSSHAIR_MODE_VALUE = {
  Normal: 0,
  Magnet: 1,
  Hidden: 2,
  MagnetOHLC: 3,
};

/** PriceScaleMode-compatible values (lightweight-charts v5). */
export const PRICE_SCALE_MODE_VALUE = {
  Normal: 0,
  Logarithmic: 1,
  Percentage: 2,
  IndexedTo100: 3,
};

export const CHART_CROSSHAIR_OPTIONS = [
  { value: "normal", label: "Crosshair" },
  { value: "magnet", label: "Magnet" },
  { value: "magnet_ohlc", label: "Magnet OHLC" },
  { value: "hidden", label: "Hidden" },
];

export const CHART_PRICE_SCALE_OPTIONS = [
  { value: "normal", label: "Linear" },
  { value: "log", label: "Log" },
  { value: "percent", label: "Percent" },
  { value: "indexed100", label: "Indexed to 100" },
];

export function crosshairModeFromValue(value) {
  switch (value) {
    case "magnet":
      return CROSSHAIR_MODE_VALUE.Magnet;
    case "magnet_ohlc":
      return CROSSHAIR_MODE_VALUE.MagnetOHLC;
    case "hidden":
      return CROSSHAIR_MODE_VALUE.Hidden;
    case "normal":
    default:
      return CROSSHAIR_MODE_VALUE.Normal;
  }
}

export function priceScaleModeFromValue(value) {
  switch (value) {
    case "log":
      return PRICE_SCALE_MODE_VALUE.Logarithmic;
    case "percent":
      return PRICE_SCALE_MODE_VALUE.Percentage;
    case "indexed100":
      return PRICE_SCALE_MODE_VALUE.IndexedTo100;
    case "normal":
    default:
      return PRICE_SCALE_MODE_VALUE.Normal;
  }
}

/** Max fraction digits for right-axis price labels (4–5 when price magnitude allows). */
export function chartPriceAxisMaxDecimals(absPrice) {
  if (!Number.isFinite(absPrice)) return 2;
  if (absPrice >= 10_000) return 2;
  if (absPrice >= 1_000) return 3;
  if (absPrice >= 100) return 4;
  return 5;
}

/**
 * Price scale + crosshair price labels. Skipped for percent / indexed-to-100 scales
 * (see `chartLocalizationForPriceScale`).
 */
export function formatChartPriceAxisLabel(price) {
  const n = typeof price === "number" ? price : Number(price);
  if (!Number.isFinite(n)) {
    return "";
  }
  const d = chartPriceAxisMaxDecimals(Math.abs(n));
  return n.toLocaleString(undefined, {
    maximumFractionDigits: d,
    minimumFractionDigits: 0,
  });
}

/**
 * @param {string} mainPriceScaleMode UI value: normal | log | percent | indexed100
 * @returns {import("lightweight-charts").DeepPartial<import("lightweight-charts").LocalizationOptionsBase>}
 */
export function chartLocalizationForPriceScale(mainPriceScaleMode) {
  if (mainPriceScaleMode === "percent" || mainPriceScaleMode === "indexed100") {
    return {};
  }
  return { priceFormatter: formatChartPriceAxisLabel };
}

/** Matches lightweight-charts internal wheel speed adjustment. */
function chartWheelSpeedAdjustment(event) {
  switch (event.deltaMode) {
    case 1: // DOM_DELTA_LINE
      return 32;
    case 2: // DOM_DELTA_PAGE
      return 120;
    default:
      return 1;
  }
}

/**
 * Base + fraction of visible bars moved per wheel “unit” (fast / speed scroll).
 * Scales up when you’re zoomed out (many bars visible) so pan stays snappy.
 */
const CHART_WHEEL_PAN_BASE = 38;
const CHART_WHEEL_PAN_FRACTION_OF_VISIBLE = 0.07;

const CHART_WHEEL_ZOOM_STEP = 0.35;

/**
 * Inertial deceleration after click-drag. Wheel is customized in
 * `attachChartWheelPan` (vertical pan + Ctrl/Cmd wheel zoom) because the
 * library only pans on horizontal wheel delta, not vertical.
 */
export const CHART_INTERACTION_DEFAULTS = {
  kineticScroll: { mouse: true, touch: true },
  handleScroll: {
    mouseWheel: false,
    pressedMouseMove: true,
    horzTouchDrag: true,
    vertTouchDrag: true,
  },
  handleScale: {
    mouseWheel: false,
    pinch: true,
    axisPressedMouseMove: true,
    axisDoubleClickReset: true,
  },
};

/**
 * @param {() => import("lightweight-charts").IChartApi | null} getChart
 * @param {HTMLElement} containerElement
 * @returns {() => void} detach — call on chart teardown
 */
export function attachChartWheelPan(getChart, containerElement) {
  let pendingPanDelta = 0;
  let pendingZoomDelta = 0;
  let rafHandle = null;

  const flush = () => {
    rafHandle = null;
    const chart = getChart();
    if (!chart) {
      pendingPanDelta = 0;
      pendingZoomDelta = 0;
      return;
    }
    const timeScale = chart.timeScale();

    if (pendingZoomDelta !== 0) {
      const visible = timeScale.getVisibleLogicalRange();
      if (visible) {
        const zoomScale = Math.sign(pendingZoomDelta) * Math.min(1, Math.abs(pendingZoomDelta));
        const width = visible.to - visible.from;
        const center = (visible.from + visible.to) / 2;
        const factor = 1 - zoomScale * CHART_WHEEL_ZOOM_STEP;
        const newWidth = Math.max(5, width * factor);
        timeScale.setVisibleLogicalRange({
          from: center - newWidth / 2,
          to: center + newWidth / 2,
        });
      }
      pendingZoomDelta = 0;
    }

    if (pendingPanDelta !== 0) {
      const pos = timeScale.scrollPosition();
      if (Number.isFinite(pos)) {
        const vis = timeScale.getVisibleLogicalRange();
        const barSpan = vis && vis.to > vis.from ? vis.to - vis.from : 80;
        const speed = CHART_WHEEL_PAN_BASE + barSpan * CHART_WHEEL_PAN_FRACTION_OF_VISIBLE;
        timeScale.scrollToPosition(pos - pendingPanDelta * speed, false);
      }
      pendingPanDelta = 0;
    }
  };

  const schedule = () => {
    if (rafHandle === null) {
      rafHandle = window.requestAnimationFrame(flush);
    }
  };

  const onWheel = (event) => {
    const chart = getChart();
    if (!chart) {
      return;
    }

    const adj = chartWheelSpeedAdjustment(event);
    const deltaX = (adj * event.deltaX) / 100;
    const deltaY = -(adj * event.deltaY) / 100;

    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      pendingZoomDelta += deltaY;
      schedule();
      return;
    }

    const combined = deltaX + deltaY;
    if (combined === 0) {
      return;
    }
    event.preventDefault();
    pendingPanDelta += combined;
    schedule();
  };

  const wheelOpts = { passive: false, capture: true };
  containerElement.addEventListener("wheel", onWheel, wheelOpts);
  return () => {
    containerElement.removeEventListener("wheel", onWheel, wheelOpts);
    if (rafHandle !== null) {
      window.cancelAnimationFrame(rafHandle);
      rafHandle = null;
    }
  };
}
